import {useMemo, useState} from 'react'
import {StyleSheet, TouchableOpacity, View} from 'react-native'
import {Image} from 'expo-image'
import {LinearGradient} from 'expo-linear-gradient'
import {AppBskyEmbedVideo, type AppBskyFeedDefs, AtUri} from '@atproto/api'
import {Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'
import {useQueryClient} from '@tanstack/react-query'

import {getVideoThumbnail} from '#/lib/media/util'
import {type NavigationProp} from '#/lib/routes/types'
import {useConsumeBillsMutation} from '#/state/queries/bills'
import {
  useCapsuleByPostQuery,
  useCapsuleByUriQuery,
} from '#/state/queries/capsule'
import {RQKEY as POST_RQKEY} from '#/state/queries/post'
import {useSession} from '#/state/session'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {BillsProvider} from '#/screens/Dashboard/contexts/BillsContext'
import {atoms as a} from '#/alf'
import BuyBillsSheet from '#/components/BuyBillsSheet'
import {Text} from '#/components/Typography'
import {type CapsuleRecentPlay, SLIDE_TYPE_META} from '#/types/capsule'

const coin_icon = require('../../../assets/capsule/images/coin.png')

/**
 * Whether a post should be drawn as a capsule.
 *
 * Exported so that places rendering posts (like Post view) can check if a post
 * contains a capsule embed.
 */
export function hasCapsuleEmbed(post: {
  record?: unknown
  capsule?: unknown
} | undefined): boolean {
  if (!post) return false
  const p = post as Record<string, unknown>
  const r = post.record as Record<string, unknown> | undefined
  return Boolean(p.capsule || r?.capsule || r?.type === 'capsule')
}

export function CapsuleFeedEmbed({
  post,
  capsule: capsuleProp,
}: {
  post: AppBskyFeedDefs.PostView
  capsule?: Record<string, unknown>
}) {
  const {t: l} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const {currentAccount} = useSession()
  const consumeBills = useConsumeBillsMutation()
  const [buyBillsOpen, setBuyBillsOpen] = useState(false)

  // capsuleProp (from the feed/thread wrapper) is the authoritative source.
  // Fall back to reading capsule off the post object for backwards compatibility.
  const capsule = useMemo(() => {
    return (
      capsuleProp ??
      ((post as unknown as Record<string, unknown>)?.capsule as
        Record<string, unknown> | undefined) ??
      ((post.record as Record<string, unknown>)?.capsule as
        Record<string, unknown> | undefined)
    )
  }, [capsuleProp, post])

  const embed = post.embed

  const thumbnail = useMemo(() => {
    if (AppBskyEmbedVideo.isView(embed)) {
      return getVideoThumbnail(embed)
    }
    return undefined
  }, [embed])

  // slideCount lives in the app.bsky.feed.capsule record, not the post.
  // New posts also have it embedded on the post record directly for fast access.
  const postRecord = post.record as Record<string, unknown>
  const postRecordSlideCount = postRecord?.slideCount as number | undefined
  const capsuleRefUri = (
    postRecord?.capsule as Record<string, unknown> | undefined
  )?.uri as string | undefined
  // needsFetch: no slide count from any fast source — must fetch the capsule record.
  const needsFetch = postRecordSlideCount == null && capsule?.slideCount == null
  // Fast path: fetch by direct URI when the post has the back-ref.
  const {data: fetchedByUri} = useCapsuleByUriQuery(
    needsFetch && capsuleRefUri ? capsuleRefUri : undefined,
  )
  // Slow fallback: list-scan when there is no direct URI (posts published without back-ref).
  const {data: fetchedByPost} = useCapsuleByPostQuery(
    needsFetch && !capsuleRefUri ? post.uri : '',
    needsFetch && !capsuleRefUri ? post.author.did : '',
  )
  const fetchedCapsule = fetchedByUri ?? fetchedByPost

  const slideCount = (capsule?.slideCount ??
    postRecordSlideCount ??
    fetchedCapsule?.slideCount) as number | undefined
  const slides = (capsule?.slides ?? fetchedCapsule?.slides) as
    Array<{type: string}> | undefined
  // Prefer slideCount (pre-computed at publish: exercise slides only).
  // Fall back to counting exercise slides from the slides array if available.
  const quizCount: number =
    slideCount ??
    (slides
      ? slides.filter(
          s =>
            SLIDE_TYPE_META[s.type as keyof typeof SLIDE_TYPE_META]?.isExercise,
        ).length
      : 0)
  const rkey = useMemo(() => new AtUri(post.uri).rkey, [post.uri])

  const recentPlays = (capsule?.recentPlays ?? []) as CapsuleRecentPlay[]
  const playCount = (capsule?.playCount ?? 0) as number

  // Up to 2 unique players by DID for avatar + name display
  const displayedPlayers = useMemo(() => {
    const seen = new Set<string>()
    const unique: CapsuleRecentPlay[] = []
    for (const play of recentPlays) {
      if (!seen.has(play.player.did)) {
        seen.add(play.player.did)
        unique.push(play)
      }
      if (unique.length >= 2) break
    }
    return unique
  }, [recentPlays])

  const othersCount = Math.max(0, playCount - displayedPlayers.length)

  const playersLabel = useMemo(() => {
    if (displayedPlayers.length === 0) return ''
    const name1 =
      displayedPlayers[0].player.displayName ??
      displayedPlayers[0].player.handle
    const name2 =
      displayedPlayers[1]?.player.displayName ??
      displayedPlayers[1]?.player.handle
    const othersWord = othersCount === 1 ? l`other` : l`others`
    if (name2) {
      if (othersCount > 0) {
        return l`${name1}, ${name2} and ${othersCount} ${othersWord} played this capsule`
      }
      return l`${name1} and ${name2} played this capsule`
    }
    if (othersCount > 0) {
      return l`${name1} and ${othersCount} ${othersWord} played this capsule`
    }
    return l`${name1} played this capsule`
  }, [displayedPlayers, othersCount, l])

  const queryClient = useQueryClient()
  const isOwnCapsule = currentAccount?.did === post.author.did

  const onPlayPress = () => {
    if (!currentAccount) return
    // Seed the post-query cache so CapsulePlay's usePostQuery resolves instantly
    // without a round-trip — avoids "can't be played" when the AppView hasn't
    // yet indexed a freshly published capsule.
    queryClient.setQueryData(POST_RQKEY(post.uri), post)
    // Navigate immediately so the UI is not blocked waiting for the API
    navigation.navigate('CapsulePlay', {
      capsuleid: rkey,
      autoPlay: true,
      authorDid: post.author.did,
      source: 'timeline',
    })
    // Owners play their own capsules for free — no bill or play record.
    if (isOwnCapsule) return
    // Consume the bill in the background. If the limit is reached, go back and
    // show the purchase sheet so the user cannot continue without taking action.
    consumeBills.mutate(
      {
        bills: 1,
        capsulecreator: post.author.did,
        capsuleId: post.uri,
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onError: (e: unknown) => {
          const msg = e instanceof Error ? e.message : ''
          if (msg === 'MAXIMUM_USAGE_REACHED') {
            navigation.goBack()
            setBuyBillsOpen(true)
          }
        },
      },
    )
  }

  return (
    <View style={styles.card}>
      {/* Background: thumbnail image */}
      {thumbnail ? (
        <Image
          source={{uri: thumbnail}}
          style={[a.absolute, a.inset_0]}
          contentFit="cover"
          accessibilityIgnoresInvertColors
          priority="high"
        />
      ) : (
        <View style={[a.absolute, a.inset_0, {backgroundColor: '#1a1a1a'}]} />
      )}

      {/* Dark gradient scrim at bottom */}
      <LinearGradient
        colors={['transparent', 'rgba(0,0,0,0.75)']}
        locations={[0.35, 1]}
        style={[a.absolute, a.inset_0]}
        pointerEvents="none"
      />

      {/* Bottom overlay: coin + button + players */}
      <View style={styles.bottomOverlay}>
        {/* Coin row */}
        <View style={styles.coinRow}>
          <Image
            source={coin_icon}
            style={styles.coinIcon}
            contentFit="contain"
          />
          <Text style={styles.coinText}>
            {quizCount} <Trans>coins to win</Trans>
          </Text>
        </View>

        {/* Play capsule button */}
        <TouchableOpacity
          style={styles.playButton}
          onPress={onPlayPress}
          activeOpacity={0.7}
          accessibilityLabel={l`Play capsule`}
          accessibilityHint={l`Opens the capsule quiz`}
          accessibilityRole="button">
          <Text style={styles.playButtonText}>
            <Trans>Play capsule</Trans>
          </Text>
        </TouchableOpacity>

        {/* Players row */}
        {playCount > 0 && (
          <View style={styles.playersRow}>
            <View style={styles.avatarStack}>
              {displayedPlayers.map((play, index) => (
                <View
                  key={play.player.did}
                  style={[
                    styles.avatarBorder,
                    index > 0 && styles.avatarOffset,
                  ]}>
                  <UserAvatar
                    type="user"
                    size={22}
                    avatar={play.player.avatar}
                  />
                </View>
              ))}
            </View>
            <Text style={styles.playersText} numberOfLines={1}>
              {playersLabel}
            </Text>
          </View>
        )}
      </View>

      <BillsProvider>
        <BuyBillsSheet
          visible={buyBillsOpen}
          onClose={() => setBuyBillsOpen(false)}
        />
      </BillsProvider>
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    aspectRatio: 3 / 4,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#111',
  },
  bottomOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 12,
    paddingBottom: 12,
    gap: 8,
  },
  coinRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  coinIcon: {
    width: 24,
    height: 24,
  },
  coinText: {
    color: 'white',
    fontSize: 13,
    fontWeight: '600',
  },
  playButton: {
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: 'white',
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  playersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  avatarStack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarBorder: {
    borderRadius: 13,
    borderWidth: 1.5,
    borderColor: 'white',
  },
  avatarOffset: {
    marginLeft: -7,
  },
  playersText: {
    flex: 1,
    color: 'white',
    fontSize: 12,
  },
})
